import React, {
  ReactElement,
  useEffect,
  useCallback,
  useState,
  useContext
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  AlertTitle,
  Typography,
  CardOkCardIcon,
  PrintAreaSwitchSidesIcon,
  Stack
} from '@customink/pigment-react';
import {Snackbar} from '@mui/material';
import Tooltip from '../CustomTooltip';
import {MobileContext} from '../../containers/MobileProvider';
import {
  useGetStoreDetailsQuery,
  ReplaceCampaignParamsType,
  useReplaceCampaignMutation
} from '../../api/storesApi';
import {useAppSelector} from '../../hooks';
import {
  selectPrimaryCampaign,
  selectReplacementCampaign
} from '../../slices/storesSlice';

type ReplacementCampaignButtonsProps = {
  storeSlug: string;
};

const ReplacementCampaignButtons: React.FC<ReplacementCampaignButtonsProps> = ({
  storeSlug
}: ReplacementCampaignButtonsProps): ReactElement => {
  useGetStoreDetailsQuery(storeSlug);
  const mobile = useContext(MobileContext);
  const primaryCampaign = useAppSelector(selectPrimaryCampaign);
  const campaignId = primaryCampaign?.id || '';
  const replacementCampaign = useAppSelector(selectReplacementCampaign);

  const [
    triggerReplaceCampaignMutation,
    {isLoading: isStartReplacementLoading, isSuccess: isStartReplacementSuccess}
  ] = useReplaceCampaignMutation();

  const [replacementDialog, setReplacementDialog] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);

  const handleOpen = useCallback(() => {
    setReplacementDialog(true);
  }, [replacementDialog]);

  const handleClose = useCallback(() => {
    setReplacementDialog(false);
  }, [replacementDialog]);

  const onCloseAlert = useCallback(() => {
    setSuccessAlert(false);
  }, [successAlert]);

  const onOpenAlert = useCallback(() => {
    setSuccessAlert(true);
  }, [successAlert]);

  useEffect(() => {
    if (isStartReplacementSuccess) {
      onOpenAlert();
    }
  }, [isStartReplacementSuccess]);

  const handleReplaceCampaign = useCallback(() => {
    const params = {
      storeSlug,
      campaignId
    } as ReplaceCampaignParamsType;

    triggerReplaceCampaignMutation(params);
    handleClose();
  }, [
    primaryCampaign,
    replacementCampaign,
    isStartReplacementLoading,
    isStartReplacementSuccess
  ]);

  const disableStartButton =
    !primaryCampaign ||
    !!replacementCampaign ||
    isStartReplacementLoading ||
    isStartReplacementSuccess;

  const disableCompleteButton = !primaryCampaign || !replacementCampaign;

  const containerClassnames = `replacementCampaignDialog-actions-buttons${
    mobile ? ' mobile' : ''
  }`;

  return (
    <div className="replacementCampaignButtons-container">
      <Tooltip
        className="replacementCampaignButtons-container-tooltip"
        title="Start a Replacement (Inker Only)"
        arrow
        placement="bottom-end">
        <span className="replacementCampaignButtons-container-actions">
          <Button
            className="replacementCampaignButtons-container-actions-button"
            aria-label="startReplacement"
            startIcon={<PrintAreaSwitchSidesIcon />}
            disabled={disableStartButton}
            size="small"
            variant="link"
            onClick={handleOpen}
          />
        </span>
      </Tooltip>
      <Dialog
        padding={{
          paddingTop: '1.75rem',
          paddingRight: '1.5rem',
          paddingBottom: '1.75rem',
          paddingLeft: '1.5rem'
        }}
        title="Start a Replacement"
        width="lg"
        open={replacementDialog}
        onClose={handleClose}>
        <DialogContent padding="1.5rem 0">
          <Alert severity="info" className="info-alert">
            <AlertTitle>You are about to start a replacement order.</AlertTitle>
            <Typography variant="bodyLong1" className="info-alert-quantity">
              By continuing, a new order will be created with a target of one
              week from today.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions className="replacementCampaignDialog-actions">
          <Stack
            alignItems={mobile ? 'center' : 'flex-end'}
            justifyContent="space-between"
            direction={mobile ? 'column' : 'row'}
            className={containerClassnames}>
            <Button
              className="dialogButton"
              variant="secondary"
              size="large"
              onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className="dialogButton"
              variant="primary"
              size="large"
              disabled={disableStartButton}
              onClick={handleReplaceCampaign}>
              Start A Replacement
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
      <Tooltip
        className="replacementCampaignButtons-tooltip"
        title="Complete Order (Inker Only)"
        arrow
        placement="bottom-end">
        <span className="replacementCampaignButtons-container-actions">
          <Button
            className="replacementCampaignButtons-container-actions-button"
            aria-label="completeReplacement"
            startIcon={<CardOkCardIcon />}
            disabled={disableCompleteButton}
            size="small"
            variant="link"
            href={
              replacementCampaign
                ? `/go/${storeSlug}/orderSummary/${replacementCampaign.id}`
                : null
            }
            target="_blank"
            rel="noreferrer"
            role="link"
          />
        </span>
      </Tooltip>
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        open={successAlert}
        autoHideDuration={4000}
        onClose={onCloseAlert}>
        <div className="successAlert-container">
          <Alert severity="success" onClose={onCloseAlert}>
            The replacement process has been successfully initiated.
          </Alert>
        </div>
      </Snackbar>
    </div>
  );
};

export default ReplacementCampaignButtons;
